import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Copy, Archive, Rocket, Trash2, 
  ChevronDown, ChevronRight, GripVertical, Star,
  Users, Sparkles, Crown, ExternalLink, Loader2,
  BarChart3, TrendingUp, Eye, UserPlus, Pencil, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import api from '../services/api';

// ============ Plan Card Component ============
const PlanCard = ({ 
  plan, 
  isDragging,
  onCopy, 
  onArchive, 
  onRestore, 
  onDelete, 
  onEdit,
  onDragStart,
  onDragEnd,
  colors,
  isInSlot = false
}) => {
  const getTypeColor = (type) => {
    switch (type) {
      case 'free': return 'bg-green-100 text-green-700';
      case 'paid': return 'bg-blue-100 text-blue-700';
      case 'inquire': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatPrice = (cents, cycle) => {
    if (!cents) return '$0';
    const dollars = cents / 100;
    return `$${dollars}${cycle === 'yearly' ? '/yr' : '/mo'}`;
  };

  return (
    <Card 
      className={cn(
        "p-4 border-2 transition-all cursor-pointer hover:shadow-md",
        colors.bgCard,
        isDragging ? "opacity-50 border-dashed" : colors.borderPrimary,
        isInSlot && "border-blue-500",
        !plan.is_deployed && "border-dashed"
      )}
      draggable={!plan.is_archived}
      onDragStart={(e) => onDragStart && onDragStart(e, plan)}
      onDragEnd={onDragEnd}
      onClick={() => !plan.is_deployed && onEdit && onEdit(plan)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {!plan.is_archived && (
            <GripVertical className={cn("w-4 h-4 cursor-grab", colors.textMuted)} />
          )}
          <div>
            {/* Title */}
            <h3 className={cn("font-semibold", colors.textPrimary)}>{plan.label}</h3>
            
            {/* Tags on new line */}
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <Badge className={getTypeColor(plan.plan_type)}>
                {plan.plan_type}
              </Badge>
              {/* Show subscriber count for archived plans */}
              {plan.is_archived && (
                <Badge variant="secondary">
                  {plan.total_subscribers || 0} Active Subscribers
                </Badge>
              )}
              {/* Show "New" only for deployed non-archived plans with 0 subs */}
              {plan.is_deployed && !plan.is_archived && (plan.total_subscribers || 0) === 0 && (
                <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                  🆕 New
                </Badge>
              )}
              {/* Show subscriber count for non-archived deployed plans */}
              {!plan.is_archived && plan.is_deployed && (plan.total_subscribers || 0) > 0 && (
                <Badge variant="secondary">
                  {plan.total_subscribers} subs
                </Badge>
              )}
              {/* Show upgrade count if any */}
              {plan.is_deployed && (plan.upgrade_count || 0) > 0 && (
                <Badge variant="outline" className="text-blue-600 border-blue-300">
                  ↑ {plan.upgrade_count} Upgrades
                </Badge>
              )}
              {/* Popular badge - only if meets_popular_criteria (100+ subs, ≤30% early churn) */}
              {plan.is_deployed && !plan.is_archived && plan.meets_popular_criteria && (
                <Badge className="bg-yellow-100 text-yellow-700">
                  <Star className="w-3 h-3 mr-1" /> Popular
                </Badge>
              )}
            </div>
            
            <div className={cn("text-lg font-bold mt-2", colors.textPrimary)}>
              {plan.plan_type === 'inquire' ? 'Contact Us' : formatPrice(plan.price_cents, plan.billing_cycle)}
              {plan.trial_days && (
                <span className={cn("text-sm font-normal ml-2", colors.textMuted)}>
                  {plan.trial_days}-day trial
                </span>
              )}
            </div>
            {plan.display?.tagline && (
              <p className={cn("text-sm mt-1", colors.textMuted)}>{plan.display.tagline}</p>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {/* Edit button for drafts */}
          {!plan.is_deployed && !plan.is_archived && (
            <Button size="icon" variant="ghost" onClick={() => onEdit(plan)} title="Edit">
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {/* Copy button - always available */}
          <Button size="icon" variant="ghost" onClick={() => onCopy(plan)} title="Copy">
            <Copy className="w-4 h-4" />
          </Button>
          {/* Archive/Delete for non-archived plans */}
          {!plan.is_archived && (
            <>
              <Button size="icon" variant="ghost" onClick={() => onArchive(plan)} title="Archive">
                <Archive className="w-4 h-4" />
              </Button>
              {!plan.is_deployed && (
                <Button size="icon" variant="ghost" onClick={() => onDelete(plan)} title="Delete">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </>
          )}
          {/* Archived plans only have Copy (no restore since deployed plans are immutable) */}
        </div>
      </div>
    </Card>
  );
};

// ============ Pricing Card Preview (mimics landing page) ============
const PricingCardPreview = ({ plan, isDefault, onRemove, onCopy, onEdit, onMoveToStaging, colors }) => {
  const formatPrice = (cents, cycle) => {
    if (!cents) return { amount: '0', period: '/PM/month' };
    const dollars = cents / 100;
    return { 
      amount: dollars.toString(), 
      period: cycle === 'yearly' ? '/PM/year' : '/PM/month' 
    };
  };
  
  const price = formatPrice(plan.price_cents, plan.billing_cycle);
  const isPopular = plan.display?.show_popular_badge;
  const isDraft = !plan.is_deployed;

  return (
    <Card className={cn(
      "relative p-6 border-2 transition-all flex flex-col h-full",
      isPopular ? "border-blue-500 shadow-lg" : colors.borderPrimary,
      isDraft && "border-dashed border-orange-300",
      colors.bgCard
    )}>
      {/* Action buttons */}
      <div className="absolute top-2 right-2 flex gap-1">
        {isDraft && (
          <>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100 text-orange-500"
              onClick={onEdit}
              title="Edit plan"
            >
              <Pencil className="w-3 h-3" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100 text-orange-500"
              onClick={onMoveToStaging}
              title="Move to staging"
            >
              <ArrowDown className="w-3 h-3" />
            </Button>
          </>
        )}
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
          onClick={onCopy}
          title="Copy plan"
        >
          <Copy className="w-3 h-3" />
        </Button>
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
          onClick={onRemove}
          title={isDraft ? "Remove from grid" : "Archive plan"}
        >
          ×
        </Button>
      </div>
      
      {/* Draft badge - left */}
      {isDraft && (
        <Badge className="absolute -top-3 left-4 bg-orange-500 text-white text-xs">
          Draft
        </Badge>
      )}
      
      {/* Default or Popular badge - center */}
      {isDefault ? (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className={isDraft ? "bg-orange-600 text-white" : "bg-blue-600 text-white"}>
            Default
          </Badge>
        </div>
      ) : isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-blue-500 text-white">Most Popular</Badge>
        </div>
      )}
      
      {/* Plan name */}
      <h3 className={cn("text-xl font-bold mt-4", colors.textPrimary)}>
        {plan.label}
      </h3>
      
      {/* Price */}
      <div className="my-2">
        {plan.plan_type === 'inquire' ? (
          <p className={cn("text-2xl font-bold", colors.textPrimary)}>Contact Us</p>
        ) : (
          <div className="flex items-baseline">
            <span className={cn("text-4xl font-bold", colors.textPrimary)}>${price.amount}</span>
            <span className={cn("text-sm ml-1", colors.textMuted)}>
              {plan.price_cents === 0 ? 'forever' : price.period}
            </span>
          </div>
        )}
      </div>
      
      {/* Tagline */}
      {plan.display?.tagline && (
        <p className={cn("text-sm mb-4", colors.textMuted)}>
          {plan.display.tagline}
        </p>
      )}
      
      {/* CTA Button - positioned after tagline, before features */}
      <Button 
        className={cn("w-full mb-4", isPopular && "bg-blue-600 hover:bg-blue-700")} 
        variant={isPopular ? "default" : "outline"}
      >
        {plan.display?.cta_text || 'Get Started'}
        <span className="ml-2">→</span>
      </Button>
      
      {/* Feature bullets */}
      <div className="flex-1">
        {plan.display?.feature_bullets?.length > 0 && (
          <ul className="space-y-2">
            {plan.display.feature_bullets.map((bullet, i) => (
              <li key={i} className={cn("flex items-start gap-2 text-sm", colors.textMuted)}>
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Footer text */}
      {plan.display?.footer_text && (
        <div className={cn("mt-4 pt-4 border-t text-sm flex items-center gap-2", colors.borderPrimary, "text-blue-600")}>
          <Users className="w-4 h-4" />
          {plan.display.footer_text}
        </div>
      )}
    </Card>
  );
};

// ============ Drop Slot Component ============
const DropSlot = ({ 
  index, 
  plan, 
  isDefault,
  isPopular,
  onDrop, 
  onRemove,
  onCopy,
  onEdit,
  onMoveToStaging,
  onSetPopular,
  colors,
  isDragOver,
  onDragOver,
  onDragLeave
}) => {
  if (plan) {
    return (
      <div className="flex flex-col h-full">
        <div
          className="flex-1"
          onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
          onDragLeave={onDragLeave}
          onDrop={(e) => { e.preventDefault(); onDrop(index); }}
        >
          <PricingCardPreview 
            plan={plan} 
            isDefault={isDefault}
            onRemove={() => onRemove(index, plan)}
            onCopy={() => onCopy(plan)}
            onEdit={() => onEdit(plan)}
            onMoveToStaging={() => onMoveToStaging(index, plan)}
            colors={colors}
          />
        </div>
        {/* Popular Toggle Button */}
        <div className="flex justify-center mt-3">
          <button
            onClick={() => onSetPopular(isPopular ? null : plan.plan_id)}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all",
              isPopular 
                ? "bg-amber-100 text-amber-700 border-2 border-amber-400" 
                : "bg-gray-100 text-gray-500 hover:bg-gray-200 border-2 border-transparent"
            )}
            title={isPopular ? "Remove 'Most Popular' badge" : "Set as 'Most Popular'"}
          >
            <Crown className={cn("w-4 h-4", isPopular && "fill-amber-500")} />
            {isPopular ? "Most Popular" : "Set Popular"}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full">
      <div
        className={cn(
          "flex-1 min-h-[480px] border-2 rounded-lg p-6 transition-all flex flex-col items-center justify-center",
          isDragOver ? "border-blue-500 bg-blue-50 dark:bg-blue-950" : "border-dashed border-gray-300",
        )}
        onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
        onDragLeave={onDragLeave}
        onDrop={(e) => { e.preventDefault(); onDrop(index); }}
      >
        {isDefault && (
          <Badge className="mb-3 bg-blue-600">
            <Star className="w-3 h-3 mr-1" /> Default Plan
          </Badge>
        )}
        <div className={cn("text-center", colors.textMuted)}>
          <Plus className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">Drop plan here</p>
          <p className="text-sm mt-1">
            {isDefault ? 'This will be the default for new signups' : `Slot ${index + 1}`}
          </p>
        </div>
      </div>
      {/* Empty state for popular toggle */}
      <div className="flex justify-center mt-3">
        <div className="px-3 py-1.5 text-sm text-gray-300">
          -
        </div>
      </div>
    </div>
  );
};

// ============ Plan Editor Modal ============
const PlanEditorModal = ({ plan, isOpen, onClose, onSave, allPlans = [], colors }) => {
  const [formData, setFormData] = useState({
    label: '',
    plan_type: 'free',
    price_cents: '',
    billing_cycle: 'month',
    trial_days: '',
    trial_access_plan_id: '',
    inquire_url: '',
    restrictions: {
      max_paid_seats: '',
      max_collaborator_seats: '',
      max_features: '',
      max_products: '',
      max_collaboration_spaces: '',
      max_stack_rankings: '',
      ai_tokens_per_month: '',
      ai_access: true,
      priority_support: false
    },
    display: {
      tagline: '',
      feature_bullets: [],
      cta_text: 'Get Started',
      show_popular_badge: false
    }
  });
  const [bulletInput, setBulletInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) {
      setFormData({
        label: plan.label || '',
        plan_type: plan.plan_type || 'free',
        price_cents: plan.price_cents?.toString() || '',
        billing_cycle: plan.billing_cycle || 'monthly',
        trial_days: plan.trial_days?.toString() || '',
        trial_access_plan_id: plan.trial_access_plan_id || '',
        inquire_url: plan.inquire_url || '',
        restrictions: {
          max_paid_seats: plan.restrictions?.max_paid_seats?.toString() || '',
          max_collaborator_seats: plan.restrictions?.max_collaborator_seats?.toString() || '',
          max_features: plan.restrictions?.max_features?.toString() || '',
          max_products: plan.restrictions?.max_products?.toString() || '',
          max_collaboration_spaces: plan.restrictions?.max_collaboration_spaces?.toString() || '',
          max_stack_rankings: plan.restrictions?.max_stack_rankings?.toString() || '',
          ai_tokens_per_month: plan.restrictions?.ai_tokens_per_month?.toString() || '',
          ai_access: plan.restrictions?.ai_access ?? true,
          priority_support: plan.restrictions?.priority_support ?? false
        },
        display: {
          tagline: plan.display?.tagline || '',
          feature_bullets: plan.display?.feature_bullets || [],
          cta_text: plan.display?.cta_text || 'Get Started',
          show_popular_badge: plan.display?.show_popular_badge ?? false
        }
      });
    } else {
      // Reset for new plan
      setFormData({
        label: '',
        plan_type: 'free',
        price_cents: '',
        billing_cycle: 'month',
        trial_days: '',
        trial_access_plan_id: '',
        inquire_url: '',
        restrictions: {
          max_paid_seats: '',
          max_collaborator_seats: '',
          max_features: '',
          max_products: '',
          max_collaboration_spaces: '',
          max_stack_rankings: '',
          ai_tokens_per_month: '',
          ai_access: true,
          priority_support: false
        },
        display: {
          tagline: '',
          feature_bullets: [],
          cta_text: 'Get Started',
          show_popular_badge: false
        }
      });
    }
  }, [plan, isOpen]);

  const handleSave = async () => {
    if (!formData.label.trim()) {
      toast.error('Plan label is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        label: formData.label,
        plan_type: formData.plan_type,
        price_cents: formData.price_cents ? parseInt(formData.price_cents) : null,
        billing_cycle: formData.plan_type === 'paid' ? formData.billing_cycle : null,
        trial_days: formData.trial_days ? parseInt(formData.trial_days) : null,
        trial_access_plan_id: formData.plan_type === 'free' && formData.trial_access_plan_id ? formData.trial_access_plan_id : null,
        inquire_url: formData.plan_type === 'inquire' ? formData.inquire_url : null,
        restrictions: {
          max_paid_seats: formData.restrictions.max_paid_seats ? parseInt(formData.restrictions.max_paid_seats) : null,
          max_collaborator_seats: formData.restrictions.max_collaborator_seats ? parseInt(formData.restrictions.max_collaborator_seats) : null,
          max_features: formData.restrictions.max_features ? parseInt(formData.restrictions.max_features) : null,
          max_products: formData.restrictions.max_products ? parseInt(formData.restrictions.max_products) : null,
          max_collaboration_spaces: formData.restrictions.max_collaboration_spaces ? parseInt(formData.restrictions.max_collaboration_spaces) : null,
          max_stack_rankings: formData.restrictions.max_stack_rankings ? parseInt(formData.restrictions.max_stack_rankings) : null,
          ai_tokens_per_month: formData.restrictions.ai_tokens_per_month ? parseInt(formData.restrictions.ai_tokens_per_month) : null,
          ai_access: formData.restrictions.ai_access,
          priority_support: formData.restrictions.priority_support
        },
        display: {
          tagline: formData.display.tagline,
          feature_bullets: formData.display.feature_bullets,
          cta_text: formData.display.cta_text,
          show_popular_badge: formData.display.show_popular_badge
        }
      };
      
      await onSave(payload, plan?.plan_id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const addBullet = () => {
    if (bulletInput.trim()) {
      setFormData(prev => ({
        ...prev,
        display: {
          ...prev.display,
          feature_bullets: [...prev.display.feature_bullets, bulletInput.trim()]
        }
      }));
      setBulletInput('');
    }
  };

  const removeBullet = (index) => {
    setFormData(prev => ({
      ...prev,
      display: {
        ...prev.display,
        feature_bullets: prev.display.feature_bullets.filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{plan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Plan Label *</Label>
              <Input 
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., Professional"
              />
            </div>
            <div className="space-y-2">
              <Label>Plan Type *</Label>
              <Select 
                value={formData.plan_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, plan_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free (No CC Required)</SelectItem>
                  <SelectItem value="paid">Paid (CC Required)</SelectItem>
                  <SelectItem value="inquire">Inquire (Contact Form)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pricing (for paid plans) */}
          {formData.plan_type === 'paid' && (
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Price (cents)</Label>
                <Input 
                  type="number"
                  value={formData.price_cents}
                  onChange={(e) => setFormData(prev => ({ ...prev, price_cents: e.target.value }))}
                  placeholder="2900 = $29"
                />
              </div>
              <div className="space-y-2">
                <Label>Billing Cycle</Label>
                <Select 
                  value={formData.billing_cycle}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, billing_cycle: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Trial Days</Label>
                <Input 
                  type="number"
                  value={formData.trial_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, trial_days: e.target.value }))}
                  placeholder="e.g., 14"
                />
              </div>
              {/* Trial Access Plan - only show for free plans with trial days */}
              {formData.plan_type === 'free' && formData.trial_days && (
                <div className="space-y-2">
                  <Label>Trial Access Plan</Label>
                  <Select
                    value={formData.trial_access_plan_id}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, trial_access_plan_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select plan for trial access" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (use free limits)</SelectItem>
                      {allPlans
                        .filter(p => p.plan_type === 'paid' && p.plan_id !== plan?.plan_id)
                        .map(p => (
                          <SelectItem key={p.plan_id} value={p.plan_id}>
                            {p.label} ({p.plan_type})
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    During trial, users get this plan&apos;s limits
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Inquire URL */}
          {formData.plan_type === 'inquire' && (
            <div className="space-y-2">
              <Label>Inquiry URL</Label>
              <Input 
                value={formData.inquire_url}
                onChange={(e) => setFormData(prev => ({ ...prev, inquire_url: e.target.value }))}
                placeholder="/contact or https://calendly.com/..."
              />
              <p className="text-xs text-gray-500">
                Use /path for internal pages, https://... for external links
              </p>
            </div>
          )}

          {/* Restrictions */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Restrictions</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Max Paid Seats</Label>
                <Input 
                  type="number"
                  value={formData.restrictions.max_paid_seats}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, max_paid_seats: e.target.value }
                  }))}
                  placeholder="Empty = unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Max Collaborator Seats</Label>
                <Input 
                  type="number"
                  value={formData.restrictions.max_collaborator_seats}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, max_collaborator_seats: e.target.value }
                  }))}
                  placeholder="Empty = unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Max Features</Label>
                <Input 
                  type="number"
                  value={formData.restrictions.max_features}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, max_features: e.target.value }
                  }))}
                  placeholder="Empty = unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Max Products</Label>
                <Input 
                  type="number"
                  value={formData.restrictions.max_products}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, max_products: e.target.value }
                  }))}
                  placeholder="Empty = unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Max Collaboration Spaces</Label>
                <Input 
                  type="number"
                  value={formData.restrictions.max_collaboration_spaces}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, max_collaboration_spaces: e.target.value }
                  }))}
                  placeholder="Empty = unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Max Stack Rankings</Label>
                <Input 
                  type="number"
                  value={formData.restrictions.max_stack_rankings}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, max_stack_rankings: e.target.value }
                  }))}
                  placeholder="Empty = unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Included AI Tokens / Month (Org-level)</Label>
                <Input 
                  type="number"
                  value={formData.restrictions.ai_tokens_per_month}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, ai_tokens_per_month: e.target.value }
                  }))}
                  placeholder="e.g., 100000"
                />
                <p className="text-xs text-muted-foreground">
                  Total AI token budget for the organization each month (not per-seat).
                </p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.restrictions.ai_access}
                  onCheckedChange={(v) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, ai_access: v }
                  }))}
                />
                <Label className="text-sm">AI Access</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.restrictions.priority_support}
                  onCheckedChange={(v) => setFormData(prev => ({
                    ...prev,
                    restrictions: { ...prev.restrictions, priority_support: v }
                  }))}
                />
                <Label className="text-sm">Priority Support</Label>
              </div>
            </div>
          </div>

          {/* Display Settings */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Display Settings</Label>
            <div className="space-y-2">
              <Label className="text-sm">Tagline</Label>
              <Input 
                value={formData.display.tagline}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  display: { ...prev.display, tagline: e.target.value }
                }))}
                placeholder="e.g., For growing teams with dedicated PMs"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">CTA Button Text</Label>
              <Input 
                value={formData.display.cta_text}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  display: { ...prev.display, cta_text: e.target.value }
                }))}
                placeholder="e.g., Start Free, Start 14-Day Trial"
              />
            </div>
            
            {/* Feature Bullets */}
            <div className="space-y-2">
              <Label className="text-sm">Feature Bullets</Label>
              <div className="flex gap-2">
                <Input 
                  value={bulletInput}
                  onChange={(e) => setBulletInput(e.target.value)}
                  placeholder="e.g., Unlimited products & goals"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBullet())}
                />
                <Button type="button" onClick={addBullet} variant="outline">Add</Button>
              </div>
              {formData.display.feature_bullets.length > 0 && (
                <ul className="space-y-1 mt-2">
                  {formData.display.feature_bullets.map((bullet, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span className="text-green-500">✓</span>
                      <span className="flex-1">{bullet}</span>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 w-6 p-0"
                        onClick={() => removeBullet(i)}
                      >
                        ×
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {plan ? 'Update Plan' : 'Create Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ Deploy Modal ============
const DeployModal = ({ isOpen, onClose, onDeploy, slots, variant, colors }) => {
  const [name, setName] = useState('');
  const [deploying, setDeploying] = useState(false);

  const handleDeploy = async () => {
    if (!name.trim()) {
      toast.error('Please enter a version name');
      return;
    }
    
    const planIds = slots.filter(Boolean).map(p => p.plan_id);
    if (planIds.length === 0) {
      toast.error('Please add at least one plan');
      return;
    }

    setDeploying(true);
    try {
      await onDeploy(variant, name, planIds);
      onClose();
      setName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deployment failed');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deploy to Pricing {variant}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Version Name</Label>
            <Input 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Holiday 2025, Standard v3"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Plans to Deploy</Label>
            <div className="space-y-2">
              {slots.map((plan, i) => plan && (
                <div key={i} className={cn("p-2 rounded border", colors.bgSecondary, colors.borderPrimary)}>
                  <span className={cn("font-medium", colors.textPrimary)}>{plan.label}</span>
                  {i === 0 && <Badge className="ml-2 bg-blue-600">Default</Badge>}
                </div>
              ))}
            </div>
          </div>

          <div className={cn("p-3 rounded-lg bg-amber-50 border border-amber-200")}>
            <p className="text-sm text-amber-800">
              <strong>Warning:</strong> Deploying will lock these plans. They cannot be edited after deployment.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleDeploy} disabled={deploying}>
            {deploying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Rocket className="w-4 h-4 mr-2" />
            Deploy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ Grid Performance Panel ============
const GridPerformancePanel = ({ slots, versionName, colors }) => {
  // Calculate aggregated metrics from slots
  const metrics = React.useMemo(() => {
    let totalSubscribers = 0;
    let paidSubscribers = 0;
    let upgrades90d = 0;
    let downgrades90d = 0;
    let cancels90d = 0;

    slots.filter(Boolean).forEach(plan => {
      const subs = plan.total_subscribers || 0;
      totalSubscribers += subs;
      
      if (plan.plan_type === 'paid') {
        paidSubscribers += subs;
      }
      
      upgrades90d += plan.upgrades_90d || 0;
      downgrades90d += plan.downgrades_90d || 0;
      cancels90d += plan.cancels_90d || 0;
    });

    return {
      totalSubscribers,
      paidSubscribers,
      upgrades90d,
      downgrades90d,
      cancels90d
    };
  }, [slots]);

  const hasPlans = slots.some(Boolean);

  if (!hasPlans) {
    return null;
  }

  return (
    <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={cn("text-sm font-semibold flex items-center gap-2", colors.textPrimary)}>
          <BarChart3 className="w-4 h-4" />
          Grid Performance
          {versionName && (
            <Badge variant="outline" className="ml-2 font-normal">
              {versionName}
            </Badge>
          )}
        </h3>
      </div>
      
      <div className="grid grid-cols-5 gap-4">
        <div className="text-center">
          <div className={cn("text-2xl font-bold", colors.textPrimary)}>
            {metrics.totalSubscribers.toLocaleString()}
          </div>
          <div className={cn("text-xs", colors.textMuted)}>Total Subs</div>
        </div>
        
        <div className="text-center">
          <div className={cn("text-2xl font-bold text-blue-600")}>
            {metrics.paidSubscribers.toLocaleString()}
          </div>
          <div className={cn("text-xs", colors.textMuted)}>Paid Subs</div>
        </div>
        
        <div className="text-center">
          <div className={cn("text-2xl font-bold text-green-600")}>
            {metrics.upgrades90d.toLocaleString()}
          </div>
          <div className={cn("text-xs", colors.textMuted)}>Upgrades (90d)</div>
        </div>
        
        <div className="text-center">
          <div className={cn("text-2xl font-bold text-orange-500")}>
            {metrics.downgrades90d.toLocaleString()}
          </div>
          <div className={cn("text-xs", colors.textMuted)}>Downgrades (90d)</div>
        </div>
        
        <div className="text-center">
          <div className={cn("text-2xl font-bold text-red-500")}>
            {metrics.cancels90d.toLocaleString()}
          </div>
          <div className={cn("text-xs", colors.textMuted)}>Cancels (90d)</div>
        </div>
      </div>
    </Card>
  );
};

// ============ Analytics Panel ============
const AnalyticsPanel = ({ analytics, colors }) => {
  if (!analytics) return null;

  const variantA = analytics.variant_a;
  const variantB = analytics.variant_b;
  const winner = variantA && variantB && 
    variantA.conversion_rate !== variantB.conversion_rate
      ? (variantA.conversion_rate > variantB.conversion_rate ? 'A' : 'B')
      : null;

  const renderVariant = (data, label, isWinner) => {
    if (!data) return (
      <div className={cn("p-4 rounded-lg border text-center", colors.bgSecondary, colors.borderPrimary)}>
        <p className={colors.textMuted}>No data for Pricing {label}</p>
      </div>
    );

    return (
      <Card className={cn("p-4 border", colors.bgCard, isWinner && "ring-2 ring-green-500")}>
        <div className="flex items-center justify-between mb-3">
          <h4 className={cn("font-semibold", colors.textPrimary)}>
            Pricing {label}: {data.name}
          </h4>
          {isWinner && <Badge className="bg-green-500">🏆 Winner</Badge>}
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <Eye className={cn("w-5 h-5 mx-auto mb-1", colors.textMuted)} />
            <p className={cn("text-2xl font-bold", colors.textPrimary)}>
              {data.visitors.toLocaleString()}
            </p>
            <p className={cn("text-xs", colors.textMuted)}>Visitors</p>
          </div>
          <div className="text-center">
            <UserPlus className={cn("w-5 h-5 mx-auto mb-1", colors.textMuted)} />
            <p className={cn("text-2xl font-bold", colors.textPrimary)}>
              {data.signups.toLocaleString()}
            </p>
            <p className={cn("text-xs", colors.textMuted)}>Signups</p>
          </div>
          <div className="text-center">
            <TrendingUp className={cn("w-5 h-5 mx-auto mb-1", colors.textMuted)} />
            <p className={cn("text-2xl font-bold", colors.textPrimary)}>
              {data.conversion_rate}%
            </p>
            <p className={cn("text-xs", colors.textMuted)}>Conversion</p>
          </div>
        </div>

        {data.plan_breakdown?.length > 0 && (
          <div className="space-y-2">
            <p className={cn("text-sm font-medium", colors.textMuted)}>By Plan:</p>
            {data.plan_breakdown.map((pb, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className={colors.textPrimary}>{pb.label}</span>
                <span className={colors.textMuted}>
                  {pb.count} ({pb.percentage.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
        <BarChart3 className="w-5 h-5" />
        A/B Performance
      </h3>
      <div className="grid grid-cols-2 gap-4">
        {renderVariant(variantA, 'A', winner === 'A')}
        {renderVariant(variantB, 'B', winner === 'B')}
      </div>
    </div>
  );
};

// ============ Main Page Component ============
const PlanManagementPage = () => {
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState({ drafts: [], deployed: [], archived: [] });
  const [config, setConfig] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  const [activeVariant, setActiveVariant] = useState('A');
  const [slotsA, setSlotsA] = useState([null, null, null]);
  const [slotsB, setSlotsB] = useState([null, null, null]);
  const [deployedSlotsA, setDeployedSlotsA] = useState([null, null, null]); // Track original deployed state
  const [deployedSlotsB, setDeployedSlotsB] = useState([null, null, null]);
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedVersions, setShowArchivedVersions] = useState(false);
  const [archivedVersions, setArchivedVersions] = useState([]);
  
  const [draggedPlan, setDraggedPlan] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deployOpen, setDeployOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [planToArchive, setPlanToArchive] = useState(null);
  const [slotToRemove, setSlotToRemove] = useState(null);

  // Check if current slots differ from deployed
  const hasChanges = (variant) => {
    const currentSlots = variant === 'A' ? slotsA : slotsB;
    const deployedSlots = variant === 'A' ? deployedSlotsA : deployedSlotsB;
    
    const currentIds = currentSlots.map(p => p?.plan_id || null);
    const deployedIds = deployedSlots.map(p => p?.plan_id || null);
    
    return JSON.stringify(currentIds) !== JSON.stringify(deployedIds);
  };

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, configRes, analyticsRes, versionsRes] = await Promise.all([
        api.get('/api/admin/plans?include_archived=true'),
        api.get('/api/admin/plans/config'),
        api.get('/api/admin/plans/analytics/ab'),
        api.get('/api/admin/plans/versions/list')
      ]);
      
      setPlans(plansRes.data);
      setConfig(configRes.data);
      setAnalytics(analyticsRes.data);
      
      // Filter to show only non-current (archived) versions
      const archived = versionsRes.data.filter(v => !v.is_current);
      setArchivedVersions(archived);
      
      // Initialize slots from current config (only on initial load or after deploy)
      if (configRes.data.current_version_a_details?.plans) {
        const aPlans = configRes.data.current_version_a_details.plans;
        const slotsArray = [aPlans[0] || null, aPlans[1] || null, aPlans[2] || null];
        setSlotsA(slotsArray);
        setDeployedSlotsA(slotsArray); // Track deployed state
      } else {
        setSlotsA([null, null, null]);
        setDeployedSlotsA([null, null, null]);
      }
      if (configRes.data.current_version_b_details?.plans) {
        const bPlans = configRes.data.current_version_b_details.plans;
        const slotsArray = [bPlans[0] || null, bPlans[1] || null, bPlans[2] || null];
        setSlotsB(slotsArray);
        setDeployedSlotsB(slotsArray);
      } else {
        setSlotsB([null, null, null]);
        setDeployedSlotsB([null, null, null]);
      }
    } catch (err) {
      console.error('Failed to fetch plan data:', err);
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/products');
      } else {
        toast.error('Failed to load plans');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Refresh only plans list without resetting slots
  const refreshPlans = useCallback(async () => {
    try {
      const plansRes = await api.get('/api/admin/plans?include_archived=true');
      setPlans(plansRes.data);
    } catch (err) {
      console.error('Failed to refresh plans:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Plan operations
  const handleCreatePlan = () => {
    setEditingPlan(null);
    setEditorOpen(true);
  };

  const handleEditPlan = (plan) => {
    if (plan.is_deployed) {
      toast.error('Cannot edit deployed plans. Use Copy instead.');
      return;
    }
    setEditingPlan(plan);
    setEditorOpen(true);
  };

  const handleSavePlan = async (data, planId) => {
    if (planId) {
      await api.put(`/api/admin/plans/${planId}`, data);
      toast.success('Plan updated');
    } else {
      await api.post('/api/admin/plans', data);
      toast.success('Plan created');
    }
    refreshPlans(); // Don't reset slots
  };

  const handleCopyPlan = async (plan) => {
    try {
      const res = await api.post(`/api/admin/plans/${plan.plan_id}/copy`);
      toast.success(`Created copy: ${res.data.label}`);
      refreshPlans(); // Don't reset slots
    } catch (err) {
      toast.error('Failed to copy plan');
    }
  };

  const handleArchivePlan = async (plan) => {
    try {
      await api.post(`/api/admin/plans/${plan.plan_id}/archive`);
      toast.success('Plan archived');
      refreshPlans(); // Don't reset slots
    } catch (err) {
      toast.error('Failed to archive plan');
    }
  };

  const handleRestorePlan = async (plan) => {
    try {
      await api.post(`/api/admin/plans/${plan.plan_id}/restore`);
      toast.success('Plan restored');
      refreshPlans(); // Don't reset slots
    } catch (err) {
      toast.error('Failed to restore plan');
    }
  };

  const handleDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;
    try {
      await api.delete(`/api/admin/plans/${planToDelete.plan_id}`);
      toast.success('Plan permanently deleted');
      refreshPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete plan');
    } finally {
      setDeleteConfirmOpen(false);
      setPlanToDelete(null);
    }
  };

  const confirmArchive = async () => {
    if (!planToDelete) return;
    try {
      await api.post(`/api/admin/plans/${planToDelete.plan_id}/archive`);
      toast.success('Plan archived');
      refreshPlans();
    } catch (err) {
      toast.error('Failed to archive plan');
    } finally {
      setDeleteConfirmOpen(false);
      setPlanToDelete(null);
    }
  };

  // Drag and drop
  const handleDragStart = (e, plan) => {
    setDraggedPlan(plan);
  };

  const handleDragEnd = () => {
    setDraggedPlan(null);
    setDragOverSlot(null);
  };

  const handleDrop = (slotIndex) => {
    if (!draggedPlan) return;
    
    const setSlots = activeVariant === 'A' ? setSlotsA : setSlotsB;
    setSlots(prev => {
      const newSlots = [...prev];
      // Remove from any existing slot
      const existingIndex = newSlots.findIndex(p => p?.plan_id === draggedPlan.plan_id);
      if (existingIndex !== -1) {
        newSlots[existingIndex] = null;
      }
      // Add to new slot
      newSlots[slotIndex] = draggedPlan;
      return newSlots;
    });
    
    setDraggedPlan(null);
    setDragOverSlot(null);
  };

  const handleRemoveFromSlot = async (slotIndex, plan) => {
    // If the plan has been deployed (in production), show archive confirmation modal
    if (plan.is_deployed) {
      setPlanToArchive(plan);
      setSlotToRemove(slotIndex);
      setArchiveConfirmOpen(true);
      return;
    }
    
    // For drafts, just remove from slot
    const setSlots = activeVariant === 'A' ? setSlotsA : setSlotsB;
    setSlots(prev => {
      const newSlots = [...prev];
      newSlots[slotIndex] = null;
      return newSlots;
    });
  };

  const confirmArchiveWithCopy = async () => {
    if (!planToArchive) return;
    
    try {
      const res = await api.post(`/api/admin/plans/${planToArchive.plan_id}/copy`);
      toast.success(`Created copy: ${res.data.label}`);
    } catch (err) {
      toast.error('Failed to copy plan');
    }
    
    await doArchiveAndRemove();
  };

  const confirmArchiveWithoutCopy = async () => {
    await doArchiveAndRemove();
  };

  const doArchiveAndRemove = async () => {
    if (!planToArchive) return;
    
    try {
      await api.post(`/api/admin/plans/${planToArchive.plan_id}/archive`);
      toast.info(`"${planToArchive.label}" archived`);
      
      // Remove from slot
      const setSlots = activeVariant === 'A' ? setSlotsA : setSlotsB;
      setSlots(prev => {
        const newSlots = [...prev];
        newSlots[slotToRemove] = null;
        return newSlots;
      });
      
      refreshPlans();
    } catch (err) {
      toast.error('Failed to archive plan');
    } finally {
      setArchiveConfirmOpen(false);
      setPlanToArchive(null);
      setSlotToRemove(null);
    }
  };

  // Move draft plan from grid back to staging (just removes from slot)
  const handleMoveToStaging = (slotIndex, plan) => {
    if (plan.is_deployed) {
      toast.error('Deployed plans cannot be moved to staging. Use Copy instead.');
      return;
    }
    
    const setSlots = activeVariant === 'A' ? setSlotsA : setSlotsB;
    setSlots(prev => {
      const newSlots = [...prev];
      newSlots[slotIndex] = null;
      return newSlots;
    });
    toast.info(`"${plan.label}" moved to staging`);
  };

  // Set a plan as "Most Popular" in the grid
  const handleSetPopular = async (planId) => {
    const currentSlots = activeVariant === 'A' ? slotsA : slotsB;
    const gridPlanIds = currentSlots.filter(Boolean).map(p => p.plan_id);
    
    try {
      await api.post('/api/admin/plans/set-popular', {
        plan_id: planId,
        grid_plan_ids: gridPlanIds
      });
      
      // Update local state to reflect the change
      const setSlots = activeVariant === 'A' ? setSlotsA : setSlotsB;
      setSlots(prev => prev.map(p => {
        if (!p) return p;
        return {
          ...p,
          display: {
            ...p.display,
            show_popular_badge: p.plan_id === planId
          }
        };
      }));
      
      if (planId) {
        const plan = currentSlots.find(p => p?.plan_id === planId);
        toast.success(`"${plan?.label}" set as Most Popular`);
      } else {
        toast.info('Popular badge cleared');
      }
    } catch (err) {
      toast.error('Failed to update popular badge');
    }
  };

  // Deploy
  const handleDeploy = async (variant, name, planIds) => {
    await api.post('/api/admin/plans/deploy', {
      variant,
      name,
      slot_plan_ids: planIds
    });
    toast.success(`Deployed to Pricing ${variant}`);
    fetchData();
  };

  // Reset grid to currently deployed state
  const handleResetGrid = () => {
    if (activeVariant === 'A') {
      setSlotsA([...deployedSlotsA]);
    } else {
      setSlotsB([...deployedSlotsB]);
    }
    toast.info('Grid reset to live state');
  };

  // Restore an archived version to the grid
  const handleRestoreVersion = async (version) => {
    try {
      // Fetch full version details with plans
      const res = await api.get(`/api/admin/plans/versions/${version.version_id}`);
      const versionData = res.data;
      
      if (!versionData.plans || versionData.plans.length === 0) {
        toast.error('No plans found in this version');
        return;
      }
      
      // Check if any plans are archived - if so, we need to copy them
      const archivedPlans = versionData.plans.filter(p => p.is_archived);
      if (archivedPlans.length > 0) {
        const shouldCopy = window.confirm(
          `${archivedPlans.length} plan(s) in this version are archived.\n\nCopies will be created for archived plans. Continue?`
        );
        if (!shouldCopy) return;
        
        // Create copies of archived plans
        for (const plan of archivedPlans) {
          try {
            await api.post(`/api/admin/plans/${plan.plan_id}/copy`);
          } catch (err) {
            console.error(`Failed to copy ${plan.label}:`, err);
          }
        }
        toast.info('Copies created for archived plans. Drag them to the grid.');
        refreshPlans();
        return;
      }
      
      // Load plans into the appropriate variant's slots
      const setSlots = version.variant === 'A' ? setSlotsA : setSlotsB;
      const slotsArray = [
        versionData.plans[0] || null,
        versionData.plans[1] || null,
        versionData.plans[2] || null
      ];
      setSlots(slotsArray);
      
      // Switch to the correct variant tab
      setActiveVariant(version.variant);
      
      toast.success(`Restored "${version.name}" to grid`);
    } catch (err) {
      console.error('Failed to restore version:', err);
      toast.error('Failed to restore version');
    }
  };

  // A/B Config
  const handleToggleAB = async (enabled) => {
    try {
      await api.put('/api/admin/plans/config', { ab_enabled: enabled });
      toast.success(enabled ? 'A/B testing enabled' : 'A/B testing disabled');
      fetchData();
    } catch (err) {
      toast.error('Failed to update config');
    }
  };

  const currentSlots = activeVariant === 'A' ? slotsA : slotsB;
  
  // Filter out plans that are currently in the active grid
  const plansInGrid = new Set([...slotsA, ...slotsB].filter(Boolean).map(p => p.plan_id));
  const allAvailablePlans = [...plans.drafts, ...plans.deployed]
    .filter(p => !p.is_archived && !plansInGrid.has(p.plan_id));

  if (loading) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Plan Management" />
          <main className="flex-1 flex items-center justify-center">
            <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Plan Management" />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Header with A/B Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>Plan Management</h1>
                <p className={cn("text-sm", colors.textMuted)}>
                  Create and manage subscription plans
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={config?.ab_enabled ?? false}
                    onCheckedChange={handleToggleAB}
                  />
                  <Label>A/B Testing</Label>
                </div>
              </div>
            </div>

            {/* A/B Tabs */}
            <Tabs value={activeVariant} onValueChange={setActiveVariant}>
              <TabsList>
                <TabsTrigger value="A">Pricing A</TabsTrigger>
                <TabsTrigger value="B" disabled={!config?.ab_enabled}>
                  Pricing B {!config?.ab_enabled && '(Enable A/B)'}
                </TabsTrigger>
              </TabsList>

              {['A', 'B'].map(variant => (
                <TabsContent key={variant} value={variant} className="space-y-6">
                  
                  {/* Active Plans Slots */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                        Active Plans
                      </h2>
                      {hasChanges(variant) ? (
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={handleResetGrid}>
                            Cancel
                          </Button>
                          <Button onClick={() => setDeployOpen(true)}>
                            <Rocket className="w-4 h-4 mr-2" />
                            Deploy Changes
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline" disabled className="text-green-600 border-green-300 cursor-default">
                          <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                          Live
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 items-stretch">
                      {(variant === 'A' ? slotsA : slotsB).map((plan, i) => (
                        <DropSlot
                          key={i}
                          index={i}
                          plan={plan}
                          isDefault={i === 0}
                          isPopular={plan?.display?.show_popular_badge}
                          onDrop={handleDrop}
                          onRemove={handleRemoveFromSlot}
                          onCopy={handleCopyPlan}
                          onEdit={handleEditPlan}
                          onMoveToStaging={handleMoveToStaging}
                          onSetPopular={handleSetPopular}
                          colors={colors}
                          isDragOver={dragOverSlot === i}
                          onDragOver={setDragOverSlot}
                          onDragLeave={() => setDragOverSlot(null)}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Grid Performance Panel */}
                  <GridPerformancePanel 
                    slots={variant === 'A' ? slotsA : slotsB}
                    versionName={config?.[`current_version_${variant.toLowerCase()}_details`]?.name}
                    colors={colors}
                  />
                </TabsContent>
              ))}
            </Tabs>

            {/* Staging Area */}
            <div className="space-y-3">
              <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                Staging Area
              </h2>
              <div className="grid grid-cols-3 gap-4">
                {allAvailablePlans.map(plan => (
                  <PlanCard
                    key={plan.plan_id}
                    plan={plan}
                    isDragging={draggedPlan?.plan_id === plan.plan_id}
                    onCopy={handleCopyPlan}
                    onArchive={handleArchivePlan}
                    onRestore={handleRestorePlan}
                    onDelete={handleDeletePlan}
                    onEdit={handleEditPlan}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    colors={colors}
                  />
                ))}
                
                {/* Add Plan Card */}
                <Card 
                  className={cn(
                    "p-4 border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors min-h-[100px]",
                    colors.borderPrimary
                  )}
                  onClick={handleCreatePlan}
                >
                  <Plus className={cn("w-8 h-8 mb-2", colors.textMuted)} />
                  <span className={colors.textMuted}>Add Plan</span>
                </Card>
              </div>
            </div>

            {/* Archived Pricing Versions */}
            {archivedVersions.length > 0 && (
              <div className="space-y-3">
                <button 
                  className={cn("flex items-center gap-2 text-sm font-medium", colors.textMuted)}
                  onClick={() => setShowArchivedVersions(!showArchivedVersions)}
                >
                  {showArchivedVersions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Archive className="w-4 h-4" />
                  Archived Pricing Versions ({archivedVersions.length})
                </button>
                
                {showArchivedVersions && (
                  <div className="grid grid-cols-3 gap-4">
                    {archivedVersions.map(version => (
                      <Card 
                        key={version.version_id} 
                        className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className={cn("font-semibold", colors.textPrimary)}>{version.name}</h4>
                              <Badge variant="outline">{version.variant}</Badge>
                            </div>
                            <p className={cn("text-xs mt-1", colors.textMuted)}>
                              {new Date(version.deployed_at).toLocaleDateString()}
                            </p>
                            <p className={cn("text-xs", colors.textMuted)}>
                              {version.slots?.length || 0} plans · {version.signups || 0} signups
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleRestoreVersion(version)}
                          >
                            Restore
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Archived Plans */}
            {plans.archived.length > 0 && (
              <div className="space-y-3">
                <button 
                  className={cn("flex items-center gap-2 text-sm font-medium", colors.textMuted)}
                  onClick={() => setShowArchived(!showArchived)}
                >
                  {showArchived ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Archive className="w-4 h-4" />
                  Archived Plans ({plans.archived.length})
                </button>
                
                {showArchived && (
                  <div className="grid grid-cols-3 gap-4">
                    {plans.archived.map(plan => (
                      <PlanCard
                        key={plan.plan_id}
                        plan={plan}
                        onCopy={handleCopyPlan}
                        onArchive={handleArchivePlan}
                        onRestore={handleRestorePlan}
                        onDelete={handleDeletePlan}
                        onEdit={handleEditPlan}
                        colors={colors}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Analytics */}
            {config?.ab_enabled && (
              <AnalyticsPanel analytics={analytics} colors={colors} />
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      <PlanEditorModal
        plan={editingPlan}
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSavePlan}
        allPlans={plans}
        colors={colors}
      />
      
      <DeployModal
        isOpen={deployOpen}
        onClose={() => setDeployOpen(false)}
        onDeploy={handleDeploy}
        slots={currentSlots}
        variant={activeVariant}
        colors={colors}
      />

      {/* Archive Confirmation Modal (for removing deployed plans from grid) */}
      <Dialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive &quot;{planToArchive?.label}&quot;?</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className={colors.textMuted}>
              This plan has been in production. Removing it from the grid will archive it.
            </p>
            <p className={colors.textMuted}>
              Would you like to create a copy before archiving?
            </p>
            
            <div className="space-y-3">
              <Card 
                className={cn(
                  "p-4 border-2 cursor-pointer hover:border-blue-500 transition-colors",
                  colors.bgCard, colors.borderPrimary
                )}
                onClick={confirmArchiveWithCopy}
              >
                <div className="flex items-start gap-3">
                  <Copy className={cn("w-5 h-5 mt-0.5 text-blue-500")} />
                  <div>
                    <h4 className={cn("font-medium", colors.textPrimary)}>Copy & Archive</h4>
                    <p className={cn("text-sm", colors.textMuted)}>
                      Create an editable copy in staging, then archive the original.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card 
                className={cn(
                  "p-4 border-2 cursor-pointer hover:border-orange-500 transition-colors",
                  colors.bgCard, colors.borderPrimary
                )}
                onClick={confirmArchiveWithoutCopy}
              >
                <div className="flex items-start gap-3">
                  <Archive className={cn("w-5 h-5 mt-0.5 text-orange-500")} />
                  <div>
                    <h4 className={cn("font-medium", colors.textPrimary)}>Archive Only</h4>
                    <p className={cn("text-sm", colors.textMuted)}>
                      Archive the plan without creating a copy.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveConfirmOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove &quot;{planToDelete?.label}&quot;?</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className={colors.textMuted}>
              What would you like to do with this plan?
            </p>
            
            <div className="space-y-3">
              <Card 
                className={cn(
                  "p-4 border-2 cursor-pointer hover:border-blue-500 transition-colors",
                  colors.bgCard, colors.borderPrimary
                )}
                onClick={confirmArchive}
              >
                <div className="flex items-start gap-3">
                  <Archive className={cn("w-5 h-5 mt-0.5", colors.textMuted)} />
                  <div>
                    <h4 className={cn("font-medium", colors.textPrimary)}>Archive</h4>
                    <p className={cn("text-sm", colors.textMuted)}>
                      Hide the plan but keep it for reference. You can copy it later.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card 
                className={cn(
                  "p-4 border-2 cursor-pointer hover:border-red-500 transition-colors",
                  colors.bgCard, colors.borderPrimary
                )}
                onClick={confirmDelete}
              >
                <div className="flex items-start gap-3">
                  <Trash2 className="w-5 h-5 mt-0.5 text-red-500" />
                  <div>
                    <h4 className={cn("font-medium text-red-600")}>Delete Permanently</h4>
                    <p className={cn("text-sm", colors.textMuted)}>
                      Remove the plan forever. This cannot be undone.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanManagementPage;
